import { clsx } from 'clsx';
import * as React from 'react';
import { useDeviceType } from '@cian/ui-kit';
import { findNotDisabledElement } from '../utils/findNotDisabledElement';
import { getDeviceStyles } from '../../styles/scrollTrack/getDeviceStyles';
import { ScrollButton } from '../ScrollButton/ScrollButton';

const ARROW_WIDTH = 48;

export const ScrollTrack = ({
  children,
  setFocusVisible,
  activeTab,
}: {
  children: React.ReactElement;
  setFocusVisible(visibility: boolean): void;
  activeTab: string;
}) => {
  const tabsListRef = React.useRef<HTMLDivElement | null>(null);

  const [needRightArrow, setNeedRightArrow] = React.useState(false);
  const [needLeftArrow, setNeedLeftArrow] = React.useState(false);

  const deviceType = useDeviceType();
  const styles = getDeviceStyles(deviceType);

  const handleScroll = ({ direction }: { direction: 1 | -1 }) => {
    const tabsList = tabsListRef.current;

    if (!tabsList) {
      return;
    }

    tabsList.scrollLeft = tabsList.scrollLeft + direction * tabsList.offsetWidth;
  };

  const scrollLeft = () => handleScroll({ direction: -1 });
  const scrollRight = () => handleScroll({ direction: 1 });

  const checkArrowsVisibility = () => {
    const tabsList = tabsListRef.current;

    if (!tabsList) {
      return;
    }

    const tabsListWidth = tabsList.scrollWidth;
    const containerWidth = tabsList.offsetWidth;
    const containerScrollLeft = tabsList.scrollLeft;

    // Показываем правую стрелку если табы не помещаются в ширину и не проскроленно до конца
    const needRightArrow = tabsListWidth > containerWidth && containerWidth + containerScrollLeft < tabsListWidth;
    const needLeftArrow = containerScrollLeft > 0;

    setNeedRightArrow(needRightArrow);
    setNeedLeftArrow(needLeftArrow);
  };

  const onScroll = () => checkArrowsVisibility();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const { target } = event;
    const tabsList: HTMLElement | null = tabsListRef.current;

    const role = (target as HTMLElement).getAttribute('role');
    if (role !== 'tab' || !tabsList) {
      return;
    }

    let newFocusTarget = null;
    setFocusVisible(true);

    switch (event.key) {
      case 'ArrowLeft':
        newFocusTarget =
          findNotDisabledElement({ target: target as HTMLElement, reverse: true }) ||
          findNotDisabledElement({ target: tabsList.lastChild as HTMLElement, reverse: true, startWithTarget: true });
        break;
      case 'ArrowRight':
        newFocusTarget =
          findNotDisabledElement({ target: target as HTMLElement }) ||
          findNotDisabledElement({ target: tabsList.firstChild as HTMLElement, reverse: false, startWithTarget: true });
        break;
      default:
        break;
    }

    if (newFocusTarget !== null) {
      (newFocusTarget as HTMLElement).focus();
      event.preventDefault();
    }
  };

  React.useEffect(() => {
    const scrollToActiveTab = () => {
      if (tabsListRef.current) {
        const childen = tabsListRef.current.childNodes;
        const activeChildenIndex = Object.keys(childen).find((index: string) => {
          return (childen[Number(index)] as HTMLElement).getAttribute('aria-selected') === 'true';
        });

        if (childen[Number(activeChildenIndex)]) {
          const trackScrollLeft = tabsListRef.current.scrollLeft;
          const trackWidth = tabsListRef.current.clientWidth;
          const itemOffsetLeft = (childen[Number(activeChildenIndex)] as HTMLElement).offsetLeft;
          const itemWidth = (childen[Number(activeChildenIndex)] as HTMLElement).clientWidth;

          if (itemOffsetLeft < trackScrollLeft + ARROW_WIDTH) {
            tabsListRef.current.scrollLeft = itemOffsetLeft - ARROW_WIDTH;
          }

          if (itemOffsetLeft + itemWidth >= trackScrollLeft + trackWidth - ARROW_WIDTH) {
            tabsListRef.current.scrollLeft = itemOffsetLeft - trackWidth + itemWidth + ARROW_WIDTH;
          }
        }
      }
    };

    checkArrowsVisibility();
    scrollToActiveTab();
  }, [deviceType, activeTab]);

  return (
    <div
      className={clsx(
        styles['wrap'],
        needLeftArrow && styles['wrap-left-blur'],
        needRightArrow && styles['wrap-right-blur'],
      )}
    >
      {needLeftArrow && <ScrollButton onClick={scrollLeft} />}
      <div
        role="tablist"
        ref={tabsListRef}
        onScroll={onScroll}
        className={clsx(styles['tabs'])}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
      {needRightArrow && <ScrollButton onClick={scrollRight} right={true} />}
    </div>
  );
};
